import { useCallback, useEffect, useRef, useState } from 'react';

import { edgeFadeMask } from '../../styles/edge-fade';
import { cn } from '../../utils/cn';
import { centeredScrollOffset } from './scroll-row-offset';
import type { ScrollRowEdges, ScrollRowProps } from './ScrollRow.types';

/** Slack, so a row scrolled to its end does not keep a hairline fade. */
const EDGE_SLACK_PX = 2;

/**
 * Web ScrollRow — a horizontally scrolling row that keeps its active item on
 * screen and softens the edges it runs off. Native mirror in
 * `ScrollRow.native.tsx`.
 *
 * The edges fade by MASKING the row's own content (`edgeFadeMask`), so they
 * dissolve into whatever fill the row sits on — a lifted track, a glass bar,
 * any dark preset. A gradient band painted over the row ramps to ONE colour
 * and showed as a dark block on every other fill. `renderEdgeFade` is a
 * native-only slot for that reason; web never draws a band.
 *
 * The scrollbar is hidden: this is a strip of controls, and a scrollbar under
 * it reads as a second, broken row. The row still scrolls by wheel, trackpad,
 * touch and keyboard.
 */
export function ScrollRow({
  children,
  activeIndex = -1,
  itemCount = 0,
  fadeEdges = false,
  className,
  contentClassName,
  testID,
}: ScrollRowProps) {
  const ref = useRef<HTMLDivElement>(null);
  const [edges, setEdges] = useState<ScrollRowEdges>({ left: false, right: false });

  useEffect(() => {
    const el = ref.current;
    if (!el || activeIndex < 0 || itemCount <= 0) return;
    const left = centeredScrollOffset({
      index: activeIndex,
      count: itemCount,
      contentWidth: el.scrollWidth,
      viewportWidth: el.clientWidth,
    });
    el.scrollTo({ left, behavior: 'smooth' });
  }, [activeIndex, itemCount]);

  const measure = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    const left = el.scrollLeft > EDGE_SLACK_PX;
    const right = el.scrollWidth - el.clientWidth - el.scrollLeft > EDGE_SLACK_PX;
    // Booleans, never the offset: a swipe then re-renders at most twice
    // instead of on every frame.
    setEdges((prev) =>
      prev.left === left && prev.right === right ? prev : { left, right },
    );
  }, []);

  useEffect(() => {
    if (!fadeEdges) return;
    measure();
    const el = ref.current;
    // Guarded: this primitive renders in test environments that ship no
    // ResizeObserver, and an unguarded `new ResizeObserver` throws out of
    // the effect and takes the whole surface down with it.
    if (!el || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [fadeEdges, measure, itemCount]);

  return (
    <div className="relative min-w-0 shrink">
      <div
        ref={ref}
        data-testid={testID}
        onScroll={fadeEdges ? measure : undefined}
        style={fadeEdges ? edgeFadeMask(edges) : undefined}
        className={cn(
          'min-w-0 overflow-x-auto overflow-y-hidden overscroll-x-contain',
          // No scrollbar under a strip of controls — it reads as a second,
          // broken row. Classes rather than an inline style: Safari and older
          // WebKit only honour `::-webkit-scrollbar`, which inline CSS cannot
          // express. `py-1 -my-1` gives the row back the few pixels
          // `overflow-y: hidden` would otherwise crop off an active pill's
          // shadow and focus ring.
          '[scrollbar-width:none] [&::-webkit-scrollbar]:hidden',
          '-my-1 py-1',
          className,
        )}>
        {/* `w-max` is what makes the row overflow instead of being squeezed:
            without it the flex children shrink to fit and the strip never
            scrolls, it just gets cut off. */}
        <div className={cn('w-max', contentClassName)}>{children}</div>
      </div>
    </div>
  );
}
(ScrollRow as { displayName?: string }).displayName = 'ScrollRow';
